import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
    Fingerprint, Calendar, Ticket, RefreshCcw, Trash2, Copy, Send, Plus,
    PartyPopper, ShieldAlert, CheckCircle2, XCircle, Upload,
} from "lucide-react";

const money = (n) => "\u20B9" + (n || 0).toLocaleString("en-IN");
const Card = ({ children, className = "" }) =>
    <div className={`card-noir p-6 lg:p-8 ${className}`}>{children}</div>;
const todayStr = () => new Date().toISOString().slice(0, 10);
const monthNow = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// =========================================================================
// BIOMETRIC TAB — ESSL x990 devices + manual push + recent events
// =========================================================================
export const BiometricTab = ({ isAdmin }) => {
    const [devices, setDevices] = useState([]);
    const [events, setEvents] = useState([]);
    const [denials, setDenials] = useState([]);
    const [members, setMembers] = useState([]);
    const [f, setF] = useState({ name: "", serial: "", location: "" });
    const [newToken, setNewToken] = useState(null);
    const [manual, setManual] = useState({ device_id: "", member_id: "", status: "Present" });

    const load = useCallback(async () => {
        const [d, e, n, m] = await Promise.all([
            api.get("/admin/biometric/devices"),
            api.get("/admin/biometric/events?limit=50"),
            api.get("/admin/biometric/denials"),
            api.get("/admin/members"),
        ]);
        setDevices(d.data); setEvents(e.data); setDenials(n.data); setMembers(m.data);
    }, []);
    useEffect(() => { load(); }, [load]);

    const addDevice = async () => {
        if (!f.name || !f.serial) return toast.error("Name & serial required");
        try {
            const { data } = await api.post("/admin/biometric/devices", f);
            setNewToken({ id: data.id, token: data.device_token, name: data.name });
            toast.success("Device registered — copy the token now!");
            setF({ name: "", serial: "", location: "" }); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    };
    const rotateToken = async (id) => {
        if (!window.confirm("Rotate token? Old token stops working immediately.")) return;
        try {
            const { data } = await api.post(`/admin/biometric/devices/${id}/rotate`);
            setNewToken({ id, token: data.device_token, name: devices.find((x) => x.id === id)?.name });
            toast.success("Token rotated — copy the new one");
        } catch (err) { toast.error(err?.response?.data?.detail || "Rotate failed"); }
    };
    const delDevice = async (id) => {
        if (!window.confirm("Deactivate this device?")) return;
        await api.delete(`/admin/biometric/devices/${id}`); toast.success("Deactivated"); load();
    };
    const sendManual = async () => {
        if (!manual.device_id || !manual.member_id) return toast.error("Pick device + member");
        const m = members.find((x) => x.id === manual.member_id);
        if (!m?.biometric_id) return toast.error("That member has no biometric_id enrolled");
        const now = new Date();
        const ts = now.toISOString().slice(0, 19).replace("T", " ");
        try {
            const { data } = await api.post("/admin/biometric/manual-push", {
                device_id: manual.device_id,
                events: [{ biometric_id: m.biometric_id, timestamp: ts, status: manual.status }],
            });
            const r = data.results[0] || {};
            toast.success(r.denied ? "Entry recorded as DENIED (no active sub)"
                                   : `Check-in logged for ${r.member}`);
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Push failed"); }
    };
    const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

    // Bulk enrollment of biometric_id from ESSL device export CSV
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkReport, setBulkReport] = useState(null);
    const onBulkFile = async (ev) => {
        const file = ev.target.files?.[0];
        ev.target.value = "";
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".csv")) {
            toast.error("Upload a .csv ESSL export"); return;
        }
        setBulkBusy(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/admin/biometric/enroll-bulk", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setBulkReport(data);
            toast.success(`Enrolled ${data.matched} · overwrote ${data.overwritten} · unmatched ${data.unmatched_count}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk enroll failed");
        }
        setBulkBusy(false);
    };

    return (
        <div className="space-y-6">
            {isAdmin && (
                <Card>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="font-mono-label"><Upload className="inline w-4 h-4 mr-1" /> Bulk-enroll biometric IDs from ESSL export</div>
                            <div className="text-xs text-sand/70 mt-1">
                                Upload the device's CSV export. We'll map each row to an existing member by phone → email → name
                                and set their <code>biometric_id</code>. Flexible headers: <code>user_id / enrollment / pin / member_no</code> for the ID.
                            </div>
                        </div>
                        <label className="btn-gold text-xs inline-flex items-center gap-1 cursor-pointer" data-testid="bio-bulk-upload">
                            <Upload className="w-3 h-3" />
                            {bulkBusy ? "Enrolling…" : "Choose CSV"}
                            <input type="file" accept=".csv,text/csv" onChange={onBulkFile}
                                   className="hidden" disabled={bulkBusy} data-testid="bio-bulk-file" />
                        </label>
                    </div>
                    {bulkReport && (
                        <div className="mt-5 grid lg:grid-cols-5 gap-3" data-testid="bio-bulk-report">
                            <div className="rounded-xl border border-emerald-400/30 p-3 text-center">
                                <div className="font-mono-label !text-[10px] text-emerald-400">Newly enrolled</div>
                                <div className="font-display text-2xl text-emerald-400 mt-1">{bulkReport.matched}</div>
                            </div>
                            <div className="rounded-xl border border-gold/30 p-3 text-center">
                                <div className="font-mono-label !text-[10px]">Already enrolled</div>
                                <div className="font-display text-2xl text-gold mt-1">{bulkReport.already_enrolled}</div>
                            </div>
                            <div className="rounded-xl border border-sand/30 p-3 text-center">
                                <div className="font-mono-label !text-[10px]">Overwritten</div>
                                <div className="font-display text-2xl text-sand mt-1">{bulkReport.overwritten}</div>
                            </div>
                            <div className="rounded-xl border border-crimson/30 p-3 text-center">
                                <div className="font-mono-label !text-[10px] text-crimson">Unmatched</div>
                                <div className="font-display text-2xl text-crimson mt-1">{bulkReport.unmatched_count}</div>
                            </div>
                            <div className="rounded-xl border border-crimson/30 p-3 text-center">
                                <div className="font-mono-label !text-[10px] text-crimson">Conflicts</div>
                                <div className="font-display text-2xl text-crimson mt-1">{bulkReport.conflicts_count}</div>
                            </div>
                        </div>
                    )}
                    {bulkReport?.unmatched?.length > 0 && (
                        <details className="mt-4">
                            <summary className="text-xs text-crimson cursor-pointer" data-testid="bio-bulk-unmatched-toggle">
                                Show {bulkReport.unmatched.length} unmatched row(s)
                            </summary>
                            <div className="mt-2 max-h-56 overflow-y-auto text-xs">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Biometric ID</TableHead><TableHead>Phone</TableHead>
                                        <TableHead>Email</TableHead><TableHead>Name</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>{bulkReport.unmatched.map((u) => (
                                        <TableRow key={`unmatched-${u.biometric_id}-${u.phone || u.email || u.name}`} data-testid={`bio-bulk-unmatched-${u.biometric_id}`}>
                                            <TableCell className="font-mono text-gold">{u.biometric_id}</TableCell>
                                            <TableCell>{u.phone || "—"}</TableCell>
                                            <TableCell>{u.email || "—"}</TableCell>
                                            <TableCell>{u.name || "—"}</TableCell>
                                        </TableRow>
                                    ))}</TableBody>
                                </Table>
                            </div>
                        </details>
                    )}
                    {bulkReport?.conflicts?.length > 0 && (
                        <details className="mt-3">
                            <summary className="text-xs text-crimson cursor-pointer" data-testid="bio-bulk-conflicts-toggle">
                                Show {bulkReport.conflicts.length} conflict(s) — biometric_id already assigned to another member
                            </summary>
                            <div className="mt-2 max-h-56 overflow-y-auto text-xs">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Biometric ID</TableHead><TableHead>Currently owned by</TableHead>
                                        <TableHead>Attempted for</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>{bulkReport.conflicts.map((c) => (
                                        <TableRow key={`conflict-${c.biometric_id}-${c.attempted_for}`} data-testid={`bio-bulk-conflict-${c.biometric_id}`}>
                                            <TableCell className="font-mono text-gold">{c.biometric_id}</TableCell>
                                            <TableCell className="text-ivory">{c.owner}</TableCell>
                                            <TableCell className="text-sand">{c.attempted_for}</TableCell>
                                        </TableRow>
                                    ))}</TableBody>
                                </Table>
                            </div>
                        </details>
                    )}
                </Card>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
                {isAdmin && (
                    <Card>
                        <div className="font-mono-label"><Fingerprint className="inline w-4 h-4 mr-1" /> Register ESSL x990 device</div>
                        <div className="space-y-3 mt-4">
                            <Input placeholder="Device name (e.g. Front Desk)" value={f.name}
                                   onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="bio-name" />
                            <Input placeholder="Serial" value={f.serial}
                                   onChange={(e) => setF({ ...f, serial: e.target.value })} data-testid="bio-serial" />
                            <Input placeholder="Location (optional)" value={f.location}
                                   onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="bio-location" />
                            <Button onClick={addDevice} className="btn-gold w-full" data-testid="bio-save">
                                <Plus className="w-4 h-4 mr-1" /> Register & generate token
                            </Button>
                        </div>
                        {newToken && (
                            <div className="mt-5 rounded-xl border border-gold/40 bg-gold/5 p-4" data-testid="bio-token-box">
                                <div className="font-mono-label text-gold">Device token · {newToken.name}</div>
                                <div className="text-[11px] text-sand/70 mt-1">Save this now. It is shown once and not retrievable.</div>
                                <div className="flex items-center gap-2 mt-3">
                                    <code className="flex-1 text-xs break-all bg-obsidian border border-gold/20 rounded p-2">{newToken.token}</code>
                                    <Button variant="ghost" onClick={() => copy(newToken.token)} data-testid="bio-token-copy">
                                        <Copy className="w-4 h-4 text-gold" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card>
                )}

                <Card>
                    <div className="font-mono-label"><Send className="inline w-4 h-4 mr-1" /> Manual check-in / fallback push</div>
                    <div className="text-xs text-sand/70 mt-1">
                        Use when the device's ADMS push stalls. Member must have a <code>biometric_id</code> enrolled.
                    </div>
                    <div className="space-y-3 mt-4">
                        <Select value={manual.device_id} onValueChange={(v) => setManual({ ...manual, device_id: v })}>
                            <SelectTrigger data-testid="bio-manual-device"><SelectValue placeholder="Device" /></SelectTrigger>
                            <SelectContent>
                                {devices.filter((d) => d.active).map((d) =>
                                    <SelectItem key={d.id} value={d.id}>{d.name} · {d.serial}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={manual.member_id} onValueChange={(v) => setManual({ ...manual, member_id: v })}>
                            <SelectTrigger data-testid="bio-manual-member"><SelectValue placeholder="Member" /></SelectTrigger>
                            <SelectContent>
                                {members.filter((m) => m.biometric_id).map((m) =>
                                    <SelectItem key={m.id} value={m.id}>{m.name} · #{m.biometric_id}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={manual.status} onValueChange={(v) => setManual({ ...manual, status: v })}>
                            <SelectTrigger data-testid="bio-manual-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["Present", "Late"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button onClick={sendManual} className="btn-gold w-full" data-testid="bio-manual-send">
                            Submit check-in
                        </Button>
                    </div>
                </Card>
            </div>

            <Card>
                <div className="font-mono-label">Devices</div>
                <Table className="mt-3">
                    <TableHeader><TableRow>
                        <TableHead>Name</TableHead><TableHead>Serial</TableHead>
                        <TableHead>Location</TableHead><TableHead>Last seen</TableHead>
                        <TableHead>Events</TableHead><TableHead>Status</TableHead>
                        {isAdmin && <TableHead></TableHead>}
                    </TableRow></TableHeader>
                    <TableBody>{devices.map((d) => (
                        <TableRow key={d.id} data-testid={`bio-row-${d.id}`}>
                            <TableCell className="text-ivory">{d.name}</TableCell>
                            <TableCell className="font-mono text-xs">{d.serial}</TableCell>
                            <TableCell className="text-sand text-sm">{d.location || "—"}</TableCell>
                            <TableCell className="text-xs text-sand/80">
                                {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString("en-IN") : "never"}
                            </TableCell>
                            <TableCell className="text-gold">{d.total_events || 0}</TableCell>
                            <TableCell className={d.active ? "text-emerald-400" : "text-sand/60"}>
                                {d.active ? "ACTIVE" : "INACTIVE"}
                            </TableCell>
                            {isAdmin && (
                                <TableCell className="flex gap-3">
                                    <button onClick={() => rotateToken(d.id)} className="text-gold text-xs" data-testid={`bio-rotate-${d.id}`}>
                                        <RefreshCcw className="inline w-3 h-3 mr-1" />Rotate
                                    </button>
                                    <button onClick={() => delDevice(d.id)} className="text-crimson text-xs" data-testid={`bio-del-${d.id}`}>
                                        <Trash2 className="inline w-3 h-3" />
                                    </button>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}{!devices.length && <TableRow><TableCell colSpan={isAdmin ? 7 : 6} className="text-sand/70 text-center py-6">
                        No devices registered yet.
                    </TableCell></TableRow>}</TableBody>
                </Table>
            </Card>

            {denials.length > 0 && (
                <Card>
                    <div className="font-mono-label text-crimson"><ShieldAlert className="inline w-4 h-4 mr-1" /> Recent denied entries (last 14 days)</div>
                    <div className="text-xs text-sand/70 mt-1">Members with an expired/missing subscription attempted entry.</div>
                    <Table className="mt-3">
                        <TableHeader><TableRow>
                            <TableHead>When</TableHead><TableHead>Member</TableHead>
                            <TableHead>Email</TableHead><TableHead>Phone</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>{denials.map((r) => (
                            <TableRow key={r.id} data-testid={`bio-denial-${r.id}`}>
                                <TableCell className="text-xs text-crimson">{new Date(r.check_in).toLocaleString("en-IN")}</TableCell>
                                <TableCell className="text-ivory">{r.member_name}</TableCell>
                                <TableCell className="text-sand text-xs">{r.member_email || "—"}</TableCell>
                                <TableCell className="text-sand text-xs">{r.member_phone || "—"}</TableCell>
                            </TableRow>
                        ))}</TableBody>
                    </Table>
                </Card>
            )}

            <Card>
                <div className="font-mono-label">Recent biometric check-ins</div>
                <Table className="mt-3">
                    <TableHeader><TableRow>
                        <TableHead>When</TableHead><TableHead>Member</TableHead>
                        <TableHead>Status</TableHead><TableHead>Device</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>{events.map((r) => (
                        <TableRow key={r.id} data-testid={`bio-event-${r.id}`}>
                            <TableCell className="text-xs text-sand/80">{new Date(r.check_in).toLocaleString("en-IN")}</TableCell>
                            <TableCell className="text-ivory">{r.member_name}</TableCell>
                            <TableCell className={r.status === "Denied" ? "text-crimson" : "text-gold"}>{r.status}</TableCell>
                            <TableCell className="text-sand text-xs">{r.device_name || "—"}</TableCell>
                        </TableRow>
                    ))}{!events.length && <TableRow><TableCell colSpan={4} className="text-sand/70 text-center py-6">
                        No biometric events yet.
                    </TableCell></TableRow>}</TableBody>
                </Table>
            </Card>
        </div>
    );
};


// =========================================================================
// HOLIDAYS TAB — Indian calendar + impact preview + manual trigger
// =========================================================================
export const HolidaysTab = ({ isAdmin }) => {
    const [rows, setRows] = useState([]);
    const [year, setYear] = useState(new Date().getFullYear());
    const [impactMonth, setImpactMonth] = useState(monthNow());
    const [impact, setImpact] = useState(null);
    const [f, setF] = useState({ date: todayStr(), name: "", type: "studio" });

    const load = useCallback(async () => setRows((await api.get(`/admin/holidays?year=${year}`)).data), [year]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get(`/admin/holidays/impact?month=${impactMonth}`).then(({ data }) => setImpact(data));
    }, [impactMonth]);

    const seed = async () => {
        try {
            const { data } = await api.post("/admin/holidays/seed");
            toast.success(`Seeded · ${data.added} added`); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Seed failed"); }
    };
    const add = async () => {
        if (!f.name || !f.date) return toast.error("Date & name required");
        try { await api.post("/admin/holidays", f);
              toast.success("Holiday added"); setF({ ...f, name: "" }); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    };
    const del = async (id) => { await api.delete(`/admin/holidays/${id}`); load(); };
    const apply = async () => {
        if (!impact || impact.extra_days <= 0) return toast.info("No extension triggered this month");
        if (!window.confirm(`Extend ${impact.affected_subscriptions} subscription(s) by ${impact.extra_days} day(s)?`)) return;
        try {
            const { data } = await api.post(`/admin/holidays/apply?month=${impactMonth}`);
            toast.success(`Extended ${data.extended} subscription(s)`);
        } catch (err) { toast.error(err?.response?.data?.detail || "Apply failed"); }
    };

    return (
        <div className="space-y-6">
            <Card>
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="font-mono-label"><PartyPopper className="inline w-4 h-4 mr-1" /> Impact preview</div>
                        <div className="text-xs text-sand/70 mt-1">
                            Rule: when a month has <strong>&gt; 2 public holidays</strong>, active subscriptions auto-extend by (holiday_count − 2) days at month-end.
                        </div>
                    </div>
                    <Input type="month" value={impactMonth} onChange={(e) => setImpactMonth(e.target.value)}
                           className="w-40" data-testid="hol-impact-month" />
                </div>
                {impact && (
                    <div className="grid lg:grid-cols-4 gap-4 mt-5">
                        <div className="rounded-xl border border-gold/30 p-4">
                            <div className="font-mono-label text-sand/70 !text-[10px]">Holidays this month</div>
                            <div className="font-display text-3xl gold-text mt-2">{impact.holiday_count}</div>
                        </div>
                        <div className="rounded-xl border border-gold/30 p-4">
                            <div className="font-mono-label text-sand/70 !text-[10px]">Extra days triggered</div>
                            <div className={`font-display text-3xl mt-2 ${impact.extra_days > 0 ? "text-emerald-400" : "text-sand/50"}`}>
                                {impact.extra_days}
                            </div>
                        </div>
                        <div className="rounded-xl border border-gold/30 p-4">
                            <div className="font-mono-label text-sand/70 !text-[10px]">Affected subscriptions</div>
                            <div className="font-display text-3xl gold-text mt-2">{impact.affected_subscriptions}</div>
                        </div>
                        <div className="rounded-xl border border-gold/30 p-4 flex items-center">
                            {isAdmin && (
                                <Button onClick={apply} disabled={impact.extra_days <= 0}
                                        className="btn-gold w-full" data-testid="hol-apply">
                                    Apply now
                                </Button>
                            )}
                        </div>
                    </div>
                )}
                {impact?.holidays?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {impact.holidays.map((h) => (
                            <span key={h.id} className="text-xs px-3 py-1 rounded-full border border-gold/30 text-sand">
                                {h.date} · <span className="text-gold">{h.name}</span>
                            </span>
                        ))}
                    </div>
                )}
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
                {isAdmin && (
                    <Card>
                        <div className="font-mono-label">Add holiday</div>
                        <div className="space-y-3 mt-4">
                            <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="hol-date" />
                            <Input placeholder="Name (e.g. Studio closed · staff training)" value={f.name}
                                   onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="hol-name" />
                            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                                <SelectTrigger data-testid="hol-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {["public", "regional", "studio"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Button onClick={add} className="btn-gold w-full" data-testid="hol-save">Add</Button>
                            <Button variant="ghost" onClick={seed} className="w-full text-sand" data-testid="hol-seed">
                                Seed Indian 2026-27 defaults
                            </Button>
                        </div>
                    </Card>
                )}
                <Card className={isAdmin ? "lg:col-span-2" : "lg:col-span-3"}>
                    <div className="flex items-center justify-between">
                        <div className="font-mono-label"><Calendar className="inline w-4 h-4 mr-1" /> {year} calendar</div>
                        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                            <SelectTrigger className="w-28" data-testid="hol-year"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {[2026, 2027, 2028].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <Table className="mt-3">
                        <TableHeader><TableRow>
                            <TableHead>Date</TableHead><TableHead>Name</TableHead>
                            <TableHead>Type</TableHead>{isAdmin && <TableHead></TableHead>}
                        </TableRow></TableHeader>
                        <TableBody>{rows.map((h) => (
                            <TableRow key={h.id} data-testid={`hol-row-${h.id}`}>
                                <TableCell className="text-gold font-mono text-xs">{h.date}</TableCell>
                                <TableCell className="text-ivory">{h.name}</TableCell>
                                <TableCell className="text-sand text-xs uppercase">{h.type}</TableCell>
                                {isAdmin && (
                                    <TableCell>
                                        <button onClick={() => del(h.id)} className="text-crimson text-xs" data-testid={`hol-del-${h.id}`}>
                                            <Trash2 className="inline w-3 h-3" />
                                        </button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}{!rows.length && <TableRow><TableCell colSpan={isAdmin ? 4 : 3} className="text-sand/70 text-center py-6">
                            No holidays for {year}. Seed the defaults or add one.
                        </TableCell></TableRow>}</TableBody>
                    </Table>
                </Card>
            </div>
        </div>
    );
};


// =========================================================================
// COUPONS TAB — percent / flat / renewal-only
// =========================================================================
export const CouponsTab = ({ isAdmin }) => {
    const [rows, setRows] = useState([]);
    const blank = { code: "", kind: "percent", value: 10, valid_from: "",
                    valid_to: "", max_uses: "", min_amount: 0, renewal_only: false, active: true };
    const [f, setF] = useState(blank);
    const load = useCallback(async () => setRows((await api.get("/admin/coupons")).data), []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!f.code) return toast.error("Code required");
        const payload = { ...f, code: f.code.toUpperCase(),
                          max_uses: f.max_uses === "" ? null : Number(f.max_uses),
                          valid_from: f.valid_from || null, valid_to: f.valid_to || null,
                          value: Number(f.value), min_amount: Number(f.min_amount) };
        try { await api.post("/admin/coupons", payload); toast.success("Coupon created"); setF(blank); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    };
    const toggle = async (c) => {
        await api.patch(`/admin/coupons/${c.id}`, { active: !c.active }); load();
    };
    const del = async (id) => {
        if (!window.confirm("Delete this coupon permanently?")) return;
        await api.delete(`/admin/coupons/${id}`); load();
    };
    const previewDiscount = useMemo(() => {
        const v = Number(f.value) || 0; const minAmt = Math.max(1000, Number(f.min_amount) || 1000);
        return f.kind === "percent" ? `${v}% off ≥ ${money(minAmt)} = ${money(minAmt * v / 100)}` : `${money(v)} off`;
    }, [f.kind, f.value, f.min_amount]);

    return (
        <div className="grid lg:grid-cols-3 gap-6">
            {isAdmin && (
                <Card>
                    <div className="font-mono-label"><Ticket className="inline w-4 h-4 mr-1" /> New coupon</div>
                    <div className="space-y-3 mt-4">
                        <Input placeholder="Code (e.g. EVE20)" value={f.code}
                               onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
                               data-testid="cp-code" />
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs text-sand/70">Kind</Label>
                                <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                                    <SelectTrigger data-testid="cp-kind"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="percent">Percent</SelectItem>
                                        <SelectItem value="flat">Flat ₹</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-sand/70">{f.kind === "percent" ? "Percent (%)" : "Amount (₹)"}</Label>
                                <Input type="number" value={f.value}
                                       onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="cp-value" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs text-sand/70">Valid from</Label>
                                <Input type="date" value={f.valid_from}
                                       onChange={(e) => setF({ ...f, valid_from: e.target.value })} data-testid="cp-from" />
                            </div>
                            <div>
                                <Label className="text-xs text-sand/70">Expiry</Label>
                                <Input type="date" value={f.valid_to}
                                       onChange={(e) => setF({ ...f, valid_to: e.target.value })} data-testid="cp-to" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs text-sand/70">Max uses</Label>
                                <Input type="number" value={f.max_uses} placeholder="∞"
                                       onChange={(e) => setF({ ...f, max_uses: e.target.value })} data-testid="cp-max" />
                            </div>
                            <div>
                                <Label className="text-xs text-sand/70">Min amount (₹)</Label>
                                <Input type="number" value={f.min_amount}
                                       onChange={(e) => setF({ ...f, min_amount: e.target.value })} data-testid="cp-min" />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-xs text-sand">
                            <input type="checkbox" checked={f.renewal_only}
                                   onChange={(e) => setF({ ...f, renewal_only: e.target.checked })}
                                   data-testid="cp-renewal" />
                            Renewal-only (applies when member has prior paid invoice)
                        </label>
                        <div className="text-[11px] text-gold">Preview · {previewDiscount}</div>
                        <Button onClick={save} className="btn-gold w-full" data-testid="cp-save">Create coupon</Button>
                    </div>
                </Card>
            )}
            <Card className={isAdmin ? "lg:col-span-2" : "lg:col-span-3"}>
                <div className="font-mono-label">All coupons</div>
                <Table className="mt-3">
                    <TableHeader><TableRow>
                        <TableHead>Code</TableHead><TableHead>Discount</TableHead>
                        <TableHead>Used</TableHead><TableHead>Window</TableHead>
                        <TableHead>Flags</TableHead><TableHead>Status</TableHead>
                        {isAdmin && <TableHead></TableHead>}
                    </TableRow></TableHeader>
                    <TableBody>{rows.map((c) => (
                        <TableRow key={c.id} data-testid={`cp-row-${c.id}`}>
                            <TableCell className="font-mono text-gold">{c.code}</TableCell>
                            <TableCell className="text-ivory">
                                {c.kind === "percent" ? `${c.value}%` : money(c.value)}
                                {c.min_amount > 0 && <span className="text-xs text-sand/60"> on ≥ {money(c.min_amount)}</span>}
                            </TableCell>
                            <TableCell className="text-sand text-sm">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</TableCell>
                            <TableCell className="text-xs text-sand/70">{c.valid_from || "—"} → {c.valid_to || "no expiry"}</TableCell>
                            <TableCell className="text-xs">
                                {c.renewal_only && <span className="px-2 py-0.5 border border-emerald-400/40 text-emerald-400 rounded-full">renewal</span>}
                            </TableCell>
                            <TableCell className={c.active ? "text-emerald-400" : "text-sand/60"}>
                                {c.active ? "ACTIVE" : "OFF"}
                            </TableCell>
                            {isAdmin && (
                                <TableCell className="flex gap-3">
                                    <button onClick={() => toggle(c)} className="text-gold text-xs" data-testid={`cp-toggle-${c.id}`}>
                                        {c.active ? "Disable" : "Enable"}
                                    </button>
                                    <button onClick={() => del(c.id)} className="text-crimson text-xs" data-testid={`cp-del-${c.id}`}>
                                        <Trash2 className="inline w-3 h-3" />
                                    </button>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}{!rows.length && <TableRow><TableCell colSpan={isAdmin ? 7 : 6} className="text-sand/70 text-center py-6">
                        No coupons yet.
                    </TableCell></TableRow>}</TableBody>
                </Table>
            </Card>
        </div>
    );
};


// =========================================================================
// OTP AUDIT TAB — today's sends / verifies / abandons + fallbacks
// =========================================================================
export const OtpAuditTab = () => {
    const [data, setData] = useState(null);
    const load = useCallback(async () => {
        try { setData((await api.get("/admin/otp/stats")).data); }
        catch (err) { toast.error("Could not load OTP stats"); }
    }, []);
    useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
    if (!data) return <div className="text-sand/70 text-sm">Loading…</div>;
    const stat = (title, val, hint) => (
        <div className="rounded-xl border border-gold/25 p-4" data-testid={`otp-stat-${title.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="font-mono-label !text-[10px] text-sand/70">{title}</div>
            <div className="font-display text-3xl gold-text mt-2">{val}</div>
            {hint && <div className="text-[10px] text-sand/60 mt-1">{hint}</div>}
        </div>
    );
    return (
        <div className="space-y-6">
            <Card>
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="font-mono-label text-gold">Today's OTP pipeline</div>
                        <div className="text-xs text-sand/70 mt-1">
                            Deliverability snapshot — refreshes every minute. Spot problems before members complain.
                        </div>
                    </div>
                    <button onClick={load} className="btn-ghost-gold text-xs" data-testid="otp-refresh">Refresh</button>
                </div>
                <div className="grid lg:grid-cols-5 gap-3 mt-5">
                    {stat("Sent", data.today.sent)}
                    {stat("Verified", data.today.verified, `${data.today.verify_rate}% verify rate`)}
                    {stat("Wrong code", data.today.failed, "1+ failed attempts")}
                    {stat("Abandoned", data.today.abandoned, "code sent, never tried")}
                    {stat("WhatsApp fallbacks", data.today.fallbacks)}
                </div>
                <div className="mt-6 grid lg:grid-cols-5 gap-3">
                    {stat("Sent (7d)", data.week.sent)}
                    {stat("Verified (7d)", data.week.verified, `${data.week.verify_rate}% verify rate`)}
                    {stat("Wrong code (7d)", data.week.failed)}
                    {stat("Abandoned (7d)", data.week.abandoned)}
                    {stat("Fallbacks (7d)", data.week.fallbacks)}
                </div>
            </Card>

            <Card>
                <div className="font-mono-label">Recent OTPs (today)</div>
                <Table className="mt-3">
                    <TableHeader><TableRow>
                        <TableHead>When</TableHead><TableHead>Email</TableHead>
                        <TableHead>Purpose</TableHead><TableHead>Attempts</TableHead>
                        <TableHead>Status</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>{data.recent.map((r) => (
                        <TableRow key={`otp-${r.email}-${r.created_at}`} data-testid={`otp-recent-${r.email}`}>
                            <TableCell className="text-xs text-sand/80">{new Date(r.created_at).toLocaleTimeString("en-IN")}</TableCell>
                            <TableCell className="text-ivory">{r.email}</TableCell>
                            <TableCell className="text-sand text-xs uppercase">{r.purpose}</TableCell>
                            <TableCell className={r.attempts > 0 ? "text-crimson" : "text-sand"}>{r.attempts}</TableCell>
                            <TableCell className={r.verified ? "text-emerald-400" : "text-sand/60"}>
                                {r.verified ? "VERIFIED" : r.attempts >= 5 ? "LOCKED" : r.attempts > 0 ? "RETRYING" : "WAITING"}
                            </TableCell>
                        </TableRow>
                    ))}{!data.recent.length && <TableRow><TableCell colSpan={5} className="text-sand/70 text-center py-6">
                        No OTP activity today.
                    </TableCell></TableRow>}</TableBody>
                </Table>
            </Card>

            {data.fallbacks.length > 0 && (
                <Card>
                    <div className="font-mono-label text-emerald-400">WhatsApp fallback requests (last 7d)</div>
                    <div className="text-xs text-sand/70 mt-1">Members who tapped "I didn't get the code". Call-back-worthy.</div>
                    <Table className="mt-3">
                        <TableHeader><TableRow>
                            <TableHead>When</TableHead><TableHead>Email</TableHead>
                            <TableHead>Phone</TableHead><TableHead>Purpose</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>{data.fallbacks.map((f) => (
                            <TableRow key={`fallback-${f.email}-${f.requested_at}`} data-testid={`otp-fallback-${f.email}`}>
                                <TableCell className="text-xs text-sand/80">{new Date(f.requested_at).toLocaleString("en-IN")}</TableCell>
                                <TableCell className="text-ivory">{f.email}</TableCell>
                                <TableCell className="text-gold text-xs">{f.phone_from_otp || "—"}</TableCell>
                                <TableCell className="text-sand text-xs uppercase">{f.purpose}</TableCell>
                            </TableRow>
                        ))}</TableBody>
                    </Table>
                </Card>
            )}
        </div>
    );
};


// =========================================================================
// COUPON VALIDATOR — moved to its own file for easier re-use outside admin
// =========================================================================
export { CouponValidator } from "./CouponValidator";
